package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import org.zq.astrock_ai.api.RecommendBoards
import org.zq.astrock_ai.api.RecommendStyle
import org.zq.astrock_ai.api.dto.LimitUpStockDto
import org.zq.astrock_ai.api.dto.RecommendPreviewItemDto
import org.zq.astrock_ai.api.dto.RecommendProgressDto
import org.zq.astrock_ai.api.dto.RecommendResponseDto
import org.zq.astrock_ai.api.dto.RecommendStockItemDto
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.isPhoneWidth
import org.zq.astrock_ai.util.formatDecimal

@Composable
fun AiRecommendDialog(
    result: RecommendResponseDto?,
    loading: Boolean,
    progress: RecommendProgressDto? = null,
    selectedBoards: Set<String>,
    selectedStyle: String = RecommendStyle.OVERNIGHT,
    watchlistCodes: Set<String> = emptySet(),
    onDismiss: () -> Unit,
    onSelectCode: (code: String, name: String) -> Unit,
    onAddWatch: (code: String, name: String) -> Unit = { _, _ -> },
    onRefresh: () -> Unit,
    onBoardsChange: (Set<String>) -> Unit,
    onStyleChange: (String) -> Unit = {},
) {
    val shape = RoundedCornerShape(14.dp)
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(if (loading) 720.dp else 520.dp)
                .fillMaxHeight(0.92f)
                .heightIn(min = 280.dp, max = 760.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(16.dp),
        ) {
            // 顶栏固定：标题 + 市场筛选始终可见
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "智启荐股",
                    color = StockColors.TextPrimary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.weight(1f),
                )
                Text(
                    "刷新",
                    color = StockColors.PrimaryBlue,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clickable(enabled = !loading, onClick = onRefresh)
                        .padding(6.dp),
                )
                Text(
                    "关闭",
                    color = StockColors.TextSecondary,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clickable(onClick = onDismiss)
                        .padding(6.dp),
                )
            }
            Spacer(Modifier.height(4.dp))
            Text(
                if (RecommendStyle.isSwing(selectedStyle)) {
                    "中线3–6个月 · 选好市场后点右上角「刷新」开始分析 · 非投资建议"
                } else {
                    "隔夜短线 · 选好市场后点右上角「刷新」开始分析 · 非投资建议"
                },
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
            Spacer(Modifier.height(8.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("持有周期", color = StockColors.TextSecondary, fontSize = 12.sp)
                BoardChip(
                    label = "隔夜短线",
                    selected = RecommendStyle.isOvernight(selectedStyle),
                    enabled = !loading,
                    onClick = { onStyleChange(RecommendStyle.OVERNIGHT) },
                )
                BoardChip(
                    label = "中线3–6月",
                    selected = RecommendStyle.isSwing(selectedStyle),
                    enabled = !loading,
                    onClick = { onStyleChange(RecommendStyle.SWING) },
                )
            }
            Spacer(Modifier.height(6.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("分析市场(单选)", color = StockColors.TextSecondary, fontSize = 12.sp)
                BoardChip(
                    label = "创业板",
                    selected = RecommendBoards.CYB in selectedBoards,
                    enabled = !loading,
                    onClick = { onBoardsChange(setOf(RecommendBoards.CYB)) },
                )
                BoardChip(
                    label = "上证主板",
                    selected = RecommendBoards.SH in selectedBoards,
                    enabled = !loading,
                    onClick = { onBoardsChange(setOf(RecommendBoards.SH)) },
                )
                BoardChip(
                    label = "深市主板",
                    selected = RecommendBoards.SZ in selectedBoards,
                    enabled = !loading,
                    onClick = { onBoardsChange(setOf(RecommendBoards.SZ)) },
                )
            }
            val boardHint = when {
                RecommendBoards.SH in selectedBoards -> "当前：上证主板全量（约1800只）"
                RecommendBoards.SZ in selectedBoards -> "当前：深市主板全量（约1500只）"
                else -> "当前：创业板全量（约1400只）"
            }
            Text(
                boardHint,
                color = StockColors.PrimaryBlue,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(Modifier.height(8.dp))
            RecommendPickTable(
                stocks = result?.stocks.orEmpty().take(5),
                loading = loading,
                onSelectCode = onSelectCode,
            )
            // hide limit-up opportunity section in recommend dialog
            Spacer(Modifier.height(8.dp))

            when {
                loading -> {
                    RecommendProgressLivePane(
                        progress = progress,
                        boardsLabel = RecommendBoards.label(selectedBoards),
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f),
                    )
                }
                result == null -> Text(
                    "点右上角「刷新」开始全量扫描与打分",
                    color = StockColors.TextSecondary,
                    fontSize = 14.sp,
                )
                else -> {
                    // 正文整段可滚：避免玩法说明挤掉名单/空仓原因，手机也能滑到底
                    LazyColumn(
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .weight(1f, fill = true),
                    ) {
                        item(key = "meta") {
                            RecommendResultMeta(result = result, selectedBoards = selectedBoards)
                        }
                        if (result.stocks.isEmpty()) {
                            item(key = "empty-title") {
                                Text(
                                    "当前市场暂无入选标的（漏斗故意可空仓）",
                                    color = StockColors.TextSecondary,
                                    fontSize = 13.sp,
                                    fontWeight = FontWeight.SemiBold,
                                )
                            }
                            if (result.emptyReasons.isNotEmpty()) {
                                item(key = "empty-reasons") {
                                    Column(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .background(Color(0xFFFFF4E5), RoundedCornerShape(8.dp))
                                            .padding(10.dp),
                                        verticalArrangement = Arrangement.spacedBy(3.dp),
                                    ) {
                                        Text(
                                            "为什么空仓",
                                            color = StockColors.Orange,
                                            fontSize = 12.sp,
                                            fontWeight = FontWeight.SemiBold,
                                        )
                                        result.emptyReasons.forEach { line ->
                                            Text(
                                                "· $line",
                                                color = StockColors.TextSecondary,
                                                fontSize = 11.sp,
                                                lineHeight = 15.sp,
                                            )
                                        }
                                    }
                                }
                            }
                            if (result.nearMisses.isNotEmpty()) {
                                item(key = "near-miss-title") {
                                    Text(
                                        "差一点入围（复盘参考，非推荐）",
                                        color = StockColors.TextPrimary,
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                }
                                items(result.nearMisses, key = { "miss-${it.code}" }) { miss ->
                                    PreviewStockRow(miss, onSelect = { onSelectCode(miss.code, miss.name) })
                                }
                            }
                            item(key = "empty-hint") {
                                Text(
                                    "可换市场或稍后盘中再刷新；空仓不代表系统故障",
                                    color = StockColors.Neutral,
                                    fontSize = 10.sp,
                                    modifier = Modifier.padding(top = 4.dp, bottom = 8.dp),
                                )
                            }
                        } else {
                            items(result.stocks, key = { it.code }) { item ->
                                RecommendStockCard(
                                    item = item,
                                    inWatchlist = item.code in watchlistCodes,
                                    onOpen = { onSelectCode(item.code, item.name) },
                                    onAddWatch = { onAddWatch(item.code, item.name) },
                                )
                            }
                            item(key = "stock-hint") {
                                Text(
                                    "点击股票名称/卡片进入研究页核对分时/资金/公告",
                                    color = StockColors.Neutral,
                                    fontSize = 10.sp,
                                    modifier = Modifier.padding(top = 4.dp, bottom = 8.dp),
                                )
                            }
                        }
                        if (result.note.isNotBlank()) {
                            item(key = "note") {
                                Text(
                                    result.note,
                                    color = StockColors.TextSecondary,
                                    fontSize = 10.sp,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RecommendResultMeta(
    result: RecommendResponseDto,
    selectedBoards: Set<String>,
) {
    val posPct = (result.recommendedPositionLimit * 100).toInt()
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(
            "${RecommendStyle.label(result.tradeStyle.ifBlank { RecommendStyle.OVERNIGHT })} · ${result.boardsLabel.ifBlank { RecommendBoards.label(selectedBoards) }} · " +
                "${result.marketRegime} · 风险${result.riskLevel}" +
                " · 候选${result.candidatePoolSize}/打分${result.scoredCount}",
            color = StockColors.TextPrimary,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Text(
            "建议仓位上限 $posPct%（宁可少买）",
            color = if (posPct <= 40) StockColors.Orange else StockColors.PrimaryBlue,
            fontSize = 13.sp,
            fontWeight = FontWeight.Bold,
            modifier = Modifier
                .fillMaxWidth()
                .background(
                    if (posPct <= 40) Color(0xFFFFF4E5) else Color(0xFFEEF4FF),
                    RoundedCornerShape(8.dp),
                )
                .padding(horizontal = 10.dp, vertical = 8.dp),
        )
        Text(
            buildString {
                append("状态 ${result.marketStatus} · ${result.generatedAt.ifBlank { "--" }}")
                if (result.liveRefresh) append(" · 实时刷新")
                if (result.northboundFlow.isNotBlank()) append(" · ${result.northboundFlow}")
                if (result.staleSources.isNotEmpty()) {
                    append(" · 时效告警：${result.staleSources.joinToString(",")}")
                }
            },
            color = if (result.staleSources.isNotEmpty()) StockColors.Orange else StockColors.TextSecondary,
            fontSize = 11.sp,
        )
        if (result.liveRefresh) {
            Text(
                "实时数据：本次名单基于最新报价/分时/资金/公告/美股与国际新闻（每次点分析都重新拉取）",
                color = StockColors.PrimaryBlue,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (result.candidateSource.isNotBlank()) {
            val srcLabel = when (result.candidateSource) {
                "eastmoney_full" -> "东财全量（休市为上一交易日快照）"
                "eastmoney_partial" -> "东财部分全量"
                "eastmoney_sample" -> "东财抽样榜"
                "cache" -> "本地候选缓存" +
                    if (result.candidateCacheAsOf.isNotBlank()) "·${result.candidateCacheAsOf}" else ""
                "tencent_seed" -> "腾讯/新浪种子池（东财瞬时不可用）"
                else -> result.candidateSource
            }
            Text(
                "候选池：$srcLabel · ${result.candidatePoolSize}只",
                color = when (result.candidateSource) {
                    "eastmoney_full" -> StockColors.TextSecondary
                    else -> StockColors.Orange
                },
                fontSize = 11.sp,
            )
        }
        val global = result.globalOvernight
        if (global.quotes.isNotEmpty() || global.headlines.isNotEmpty() ||
            global.mappingNotes.isNotEmpty() || global.hotSectors.isNotEmpty()
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF7F3EA), RoundedCornerShape(8.dp))
                    .padding(10.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                Text(
                    "隔夜外盘·美股热门→A股" + if (global.asOf.isNotBlank()) " · ${global.asOf}" else "",
                    color = StockColors.TextPrimary,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                if (global.hotSectors.isNotEmpty()) {
                    global.hotSectors.take(6).forEach { sec ->
                        val dir = if (sec.hot) "热门" else "承压"
                        val ash = sec.aShareHints.take(3).joinToString("/")
                        Text(
                            "· [$dir] ${sec.name} ${formatDecimal(sec.changePct, 2, forceSign = true)}% · ${sec.theme}" +
                                if (ash.isNotBlank()) " → A股关注 $ash" else "",
                            color = if (sec.hot) StockColors.UpRed else StockColors.DownGreen,
                            fontSize = 10.sp,
                            lineHeight = 14.sp,
                        )
                    }
                }
                global.mappingNotes.take(8).forEach { note ->
                    Text("· $note", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
                }
            }
            Spacer(Modifier.height(8.dp))
        }
        if (result.playbook.isNotEmpty()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF3F6FA), RoundedCornerShape(8.dp))
                    .padding(10.dp),
                verticalArrangement = Arrangement.spacedBy(3.dp),
            ) {
                Text(
                    "怎么用这份名单",
                    color = StockColors.TextPrimary,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                result.playbook.forEach { tip ->
                    Text("· $tip", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
                }
            }
        }
    }
}

@Composable
private fun RecommendPickTable(
    stocks: List<RecommendStockItemDto>,
    loading: Boolean,
    onSelectCode: (code: String, name: String) -> Unit,
) {
    val headerBg = Color(0xFFF3F6FA)
    val rowBorder = Color(0xFFE8EEF7)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(BorderStroke(1.dp, StockColors.Border), RoundedCornerShape(8.dp)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(headerBg, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))
                .padding(horizontal = 8.dp, vertical = 7.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TableHeaderCell("股票", Modifier.weight(1.35f))
            TableHeaderCell("当前价", Modifier.weight(0.85f), alignEnd = true)
            TableHeaderCell("买入价", Modifier.weight(1.1f), alignEnd = true)
            TableHeaderCell("卖出价", Modifier.weight(1.1f), alignEnd = true)
        }
        when {
            loading && stocks.isEmpty() -> {
                Text(
                    "分析中，入选后显示…",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 10.dp, vertical = 12.dp),
                )
            }
            stocks.isEmpty() -> {
                Text(
                    "暂无入选标的",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 10.dp, vertical = 12.dp),
                )
            }
            else -> {
                stocks.forEachIndexed { idx, item ->
                    val buy = when {
                        item.buyLow > 0 && item.buyHigh > 0 ->
                            "${formatDecimal(item.buyLow)}–${formatDecimal(item.buyHigh)}"
                        item.buyLow > 0 -> formatDecimal(item.buyLow)
                        item.buyHigh > 0 -> formatDecimal(item.buyHigh)
                        else -> "--"
                    }
                    val sell = when {
                        item.sellLow > 0 && item.sellHigh > 0 ->
                            "${formatDecimal(item.sellLow)}–${formatDecimal(item.sellHigh)}"
                        item.sellLow > 0 -> formatDecimal(item.sellLow)
                        item.sellHigh > 0 -> formatDecimal(item.sellHigh)
                        else -> "--"
                    }
                    if (idx > 0) {
                        Spacer(
                            Modifier
                                .fillMaxWidth()
                                .height(1.dp)
                                .background(rowBorder),
                        )
                    }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable { onSelectCode(item.code, item.name) }
                            .padding(horizontal = 8.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Column(modifier = Modifier.weight(1.35f)) {
                            Text(
                                item.name.ifBlank { item.code },
                                color = StockColors.PrimaryBlue,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                            )
                            Text(
                                "${item.code} · 点进详情",
                                color = StockColors.TextSecondary,
                                fontSize = 10.sp,
                                maxLines = 1,
                            )
                        }
                        Text(
                            if (item.price > 0) formatDecimal(item.price) else "--",
                            color = StockColors.TextPrimary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier.weight(0.85f),
                            textAlign = TextAlign.End,
                        )
                        Text(
                            buy,
                            color = StockColors.PrimaryBlue,
                            fontSize = 11.sp,
                            modifier = Modifier.weight(1.1f),
                            textAlign = TextAlign.End,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                        Text(
                            sell,
                            color = StockColors.UpRed,
                            fontSize = 11.sp,
                            modifier = Modifier.weight(1.1f),
                            textAlign = TextAlign.End,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                    }
                    Text(
                        buildString {
                            append("市值${formatTurnover(item.marketCap)}")
                            append(" · 流通${formatTurnover(item.floatCap)}")
                            append(" · 市盈${formatPe(item.pe)}")
                            append(" · 量比${if (item.volumeRatio > 0) formatDecimal(item.volumeRatio) else "--"}")
                            append(" · 换手${if (item.turnoverRate > 0) formatDecimal(item.turnoverRate) + "%" else "--"}")
                            append(" · 额${formatTurnover(item.turnover)}")
                            append(" · 净流入${formatMainNet(item.todayMainNet)}")
                        },
                        color = StockColors.TextSecondary,
                        fontSize = 10.sp,
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(start = 8.dp, end = 8.dp, bottom = 8.dp),
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
        }
    }
}

/** 当前所选创业板/上证内的涨停与接近涨停（非新市场入口） */
@Composable
private fun LimitUpOpportunitySection(
    items: List<LimitUpStockDto>,
    boardsLabel: String,
    asOf: String,
    onSelectCode: (code: String, name: String) -> Unit,
) {
    val headerBg = Color(0xFFFFF5F5)
    val rowBorder = Color(0xFFF0E0E0)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(BorderStroke(1.dp, Color(0xFFF0C8C8)), RoundedCornerShape(8.dp)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(headerBg, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))
                .padding(horizontal = 8.dp, vertical = 7.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    "${boardsLabel}·涨停机会",
                    color = StockColors.UpRed,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    asOf.ifBlank { "已涨停多久封板 / 接近涨停还差多少（行情参考）" },
                    color = StockColors.TextSecondary,
                    fontSize = 10.sp,
                )
            }
        }
        if (items.isEmpty()) {
            Text(
                "本市场暂无涨停或接近涨停标的",
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 10.dp, vertical = 10.dp),
            )
        } else {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 8.dp, vertical = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                TableHeaderCell("股票", Modifier.weight(1.2f))
                TableHeaderCell("涨幅", Modifier.weight(0.7f), alignEnd = true)
                TableHeaderCell("多久涨停", Modifier.weight(1.1f), alignEnd = true)
            }
            items.take(12).forEachIndexed { idx, item ->
                if (idx > 0) {
                    Spacer(
                        Modifier
                            .fillMaxWidth()
                            .height(1.dp)
                            .background(rowBorder),
                    )
                }
                val timing = when {
                    item.kind == "near" -> item.sealAfterOpen.ifBlank {
                        "距涨停约${formatDecimal(item.toLimitPct)}%"
                    }
                    item.firstLimitTime.isNotBlank() && item.sealAfterOpen.isNotBlank() ->
                        "${item.firstLimitTime}·${item.sealAfterOpen}"
                    item.sealAfterOpen.isNotBlank() -> item.sealAfterOpen
                    item.firstLimitTime.isNotBlank() -> item.firstLimitTime
                    else -> "—"
                }
                val tag = if (item.kind == "near") "接近" else "涨停"
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onSelectCode(item.code, item.name) }
                        .padding(horizontal = 8.dp, vertical = 7.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1.2f)) {
                        Text(
                            item.name.ifBlank { item.code },
                            color = StockColors.PrimaryBlue,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Medium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                        Text(
                            "${item.code} · $tag",
                            color = StockColors.TextSecondary,
                            fontSize = 10.sp,
                        )
                    }
                    Text(
                        "${if (item.changePct >= 0) "+" else ""}${formatDecimal(item.changePct)}%",
                        color = StockColors.UpRed,
                        fontSize = 11.sp,
                        modifier = Modifier.weight(0.7f),
                        textAlign = TextAlign.End,
                    )
                    Text(
                        timing,
                        color = if (item.kind == "sealed") StockColors.UpRed else StockColors.Orange,
                        fontSize = 11.sp,
                        modifier = Modifier.weight(1.1f),
                        textAlign = TextAlign.End,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                    )
                }
            }
            Text(
                "说明：非隔夜荐股；已涨停=首次封板用时，接近=距涨停空间",
                color = StockColors.Neutral,
                fontSize = 9.sp,
                modifier = Modifier.padding(horizontal = 8.dp, vertical = 6.dp),
            )
        }
    }
}

@Composable
private fun TableHeaderCell(
    label: String,
    modifier: Modifier = Modifier,
    alignEnd: Boolean = false,
) {
    Text(
        label,
        color = StockColors.TextSecondary,
        fontSize = 11.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = modifier,
        textAlign = if (alignEnd) TextAlign.End else TextAlign.Start,
    )
}

@Composable
private fun BoardChip(
    label: String,
    selected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    val bg = if (selected) StockColors.PrimaryBlue.copy(alpha = 0.12f) else Color.Transparent
    val border = if (selected) StockColors.PrimaryBlue else StockColors.Border
    val fg = if (selected) StockColors.PrimaryBlue else StockColors.TextSecondary
    Text(
        label,
        color = fg,
        fontSize = 12.sp,
        fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
        modifier = Modifier
            .background(bg, RoundedCornerShape(999.dp))
            .border(BorderStroke(1.dp, border), RoundedCornerShape(999.dp))
            .clickable(enabled = enabled, onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 5.dp),
    )
}

@Composable
private fun TagChip(label: String, fg: Color, bg: Color) {
    Text(
        label,
        color = fg,
        fontSize = 10.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .background(bg, RoundedCornerShape(6.dp))
            .padding(horizontal = 6.dp, vertical = 2.dp),
    )
}

private fun formatTurnover(v: Double): String = when {
    v >= 1e8 -> "${formatDecimal(v / 1e8, 1)}亿"
    v >= 1e4 -> "${formatDecimal(v / 1e4, 0)}万"
    v > 0 -> formatDecimal(v, 0)
    else -> "--"
}

private fun formatPe(v: Double): String = when {
    v == 0.0 -> "--"
    else -> formatDecimal(v, 1)
}

private fun formatMainNet(v: Double): String = when {
    v > 0 -> "+${formatTurnover(v)}"
    v < 0 -> "-${formatTurnover(kotlin.math.abs(v))}"
    else -> "--"
}

@Composable
private fun RecommendQuoteMetrics(item: RecommendStockItemDto) {
    Text(
        buildString {
            append("市值 ${formatTurnover(item.marketCap)}")
            append(" · 流通 ${formatTurnover(item.floatCap)}")
            append(" · 市盈 ${formatPe(item.pe)}")
            append(" · 量比 ${if (item.volumeRatio > 0) formatDecimal(item.volumeRatio) else "--"}")
            append(" · 换手 ${if (item.turnoverRate > 0) formatDecimal(item.turnoverRate) + "%" else "--"}")
            append(" · 额 ${formatTurnover(item.turnover)}")
            append(" · 净流入 ${formatMainNet(item.todayMainNet)}")
        },
        color = StockColors.TextPrimary,
        fontSize = 11.sp,
        fontWeight = FontWeight.SemiBold,
    )
}

@Composable
private fun RecommendStockCard(
    item: RecommendStockItemDto,
    inWatchlist: Boolean,
    onOpen: () -> Unit,
    onAddWatch: () -> Unit,
) {
    var expanded by remember(item.code) { mutableStateOf(false) }
    val signalColor = when (item.signal.uppercase()) {
        "BUY" -> StockColors.UpRed
        "SELL" -> StockColors.DownGreen
        else -> StockColors.TextSecondary
    }
    val signalLabel = when (item.signal.uppercase()) {
        "BUY" -> "买"
        "SELL" -> "卖"
        else -> "观望"
    }
    val hasBuyZone = item.buyLow > 0 && item.buyHigh > 0
    val hasSellZone = item.sellLow > 0 && item.sellHigh > 0
    val summary = remember(item) { conciseRecommendSummary(item) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
            .clickable(onClick = onOpen)
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        // 第一行：名称 + 分数 + 加自选
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .clickable(onClick = onOpen),
            ) {
                Text(
                    item.name.ifBlank { item.code },
                    color = StockColors.PrimaryBlue,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 15.sp,
                    maxLines = 1,
                )
                Text(
                    "${item.code} · ${item.board} · 点名称进详情",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    maxLines = 1,
                )
            }
            Text(
                "详情",
                color = Color.White,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier
                    .background(StockColors.PrimaryBlue, RoundedCornerShape(999.dp))
                    .clickable(onClick = onOpen)
                    .padding(horizontal = 10.dp, vertical = 6.dp),
            )
            Spacer(Modifier.padding(start = 6.dp))
            Text(
                formatDecimal(item.score),
                color = StockColors.PrimaryBlue,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
            )
            Spacer(Modifier.padding(start = 8.dp))
            Text(
                if (inWatchlist) "已自选" else "+自选",
                color = if (inWatchlist) StockColors.DownGreen else Color.White,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier
                    .background(
                        if (inWatchlist) StockColors.DownSoft else StockColors.PrimaryBlue,
                        RoundedCornerShape(999.dp),
                    )
                    .clickable(enabled = !inWatchlist, onClick = onAddWatch)
                    .padding(horizontal = 10.dp, vertical = 6.dp),
            )
        }

        // 第二行：价 / 涨跌 / 信号 + 隔夜标签
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                formatDecimal(item.price),
                color = StockColors.TextPrimary,
                fontWeight = FontWeight.Bold,
                fontSize = 14.sp,
            )
            Spacer(Modifier.padding(start = 8.dp))
            Text(
                "${formatDecimal(item.changePct)}%",
                color = if (item.changePct >= 0) StockColors.UpRed else StockColors.DownGreen,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(Modifier.padding(start = 8.dp))
            Text(
                signalLabel,
                color = signalColor,
                fontSize = 11.sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier
                    .background(
                        when (item.signal.uppercase()) {
                            "BUY" -> StockColors.UpSoft
                            "SELL" -> StockColors.DownSoft
                            else -> Color(0xFFF3F4F6)
                        },
                        RoundedCornerShape(6.dp),
                    )
                    .padding(horizontal = 6.dp, vertical = 2.dp),
            )
            Spacer(Modifier.padding(start = 6.dp))
            if (item.swingFit) {
                TagChip("适合中线", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.limitHuntFit) {
                TagChip("次日上涨", StockColors.UpRed, Color(0xFFFFE8E8))
            }
            if (item.overnightFit) {
                TagChip("适合隔夜", StockColors.UpRed, StockColors.UpSoft)
            }
            if (!item.swingFit && !item.overnightFit && !item.limitHuntFit) {
                TagChip("风格一般", StockColors.Neutral, Color(0xFFF3F4F6))
            }
            if (item.deepRebound) {
                TagChip("深跌反抽", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.morningDipRebound) {
                TagChip("早盘翻红", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.volumeDryStop) {
                TagChip("缩量止跌", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.sectorLeaderStart) {
                TagChip("龙头启动", StockColors.DownGreen, Color(0xFFE8F8F0))
            }
            if (item.lowAbsorbRebound) {
                TagChip("低吸反抽", StockColors.Orange, Color(0xFFFFF4E5))
            }
            Spacer(Modifier.weight(1f))
            Text(
                "置信 ${formatDecimal(item.confidence * 100, 0)}%",
                color = StockColors.Neutral,
                fontSize = 11.sp,
            )
        }

        // 精简总结（默认可见）
        Text(
            summary,
            color = StockColors.TextPrimary,
            fontSize = 12.sp,
            lineHeight = 17.sp,
            maxLines = if (expanded) 8 else 3,
            overflow = TextOverflow.Ellipsis,
        )
        if (item.sectorLeaderCode.isNotBlank() || item.sectorLeaderLabel.isNotBlank()) {
            Text(
                buildString {
                    append("龙头联动：")
                    if (item.sectorLeaderName.isNotBlank()) {
                        append(item.sectorLeaderName)
                        if (item.sectorLeaderCode.isNotBlank()) append("(${item.sectorLeaderCode})")
                        append(" ${formatDecimal(item.sectorLeaderChangePct, 2)}%")
                    }
                    if (item.sectorBoardChangePct != 0.0) {
                        append(" · 板块${formatDecimal(item.sectorBoardChangePct, 2)}%")
                    }
                    if (item.sectorLeaderLabel.isNotBlank()) {
                        append(" · ")
                        append(item.sectorLeaderLabel.removePrefix("龙头："))
                    }
                },
                color = when {
                    item.sectorLeaderLabel.contains("偏强") || item.sectorLeaderLabel.contains("带动") ||
                        item.sectorLeaderLabel.contains("自身") -> StockColors.DownGreen
                    item.sectorLeaderLabel.contains("滞涨") || item.sectorLeaderLabel.contains("弱") ||
                        item.sectorLeaderLabel.contains("风险") -> StockColors.Orange
                    else -> StockColors.TextSecondary
                },
                fontSize = 11.sp,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // 建议买入 / 建议卖出（默认可见）
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(StockColors.UpSoft, RoundedCornerShape(8.dp))
                .padding(horizontal = 10.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "建议买入",
                color = StockColors.UpRed,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(Modifier.weight(1f))
            Text(
                if (hasBuyZone) {
                    "${formatDecimal(item.buyLow)}–${formatDecimal(item.buyHigh)}"
                } else {
                    "--"
                },
                color = StockColors.UpRed,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
            )
        }
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(StockColors.DownSoft, RoundedCornerShape(8.dp))
                .padding(horizontal = 10.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                "建议卖出",
                color = StockColors.DownGreen,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(Modifier.weight(1f))
            Text(
                if (hasSellZone) {
                    "${formatDecimal(item.sellLow)}–${formatDecimal(item.sellHigh)}"
                } else {
                    "--"
                },
                color = StockColors.DownGreen,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
            )
        }
        if (item.sellPerspectives.isNotEmpty()) {
            Text(
                "多方：" + item.sellPerspectives.take(4).joinToString(" · ") {
                    it.substringBefore("（").trim()
                },
                color = StockColors.TextSecondary,
                fontSize = 10.sp,
                lineHeight = 14.sp,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )
        }
        if (hasSellZone && item.sellMethod.isNotBlank()) {
            Text(
                item.sellMethod,
                color = StockColors.TextSecondary,
                fontSize = 10.sp,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // 展开 / 收起
        Text(
            if (expanded) "收起详情 ▲" else "展开详情 ▼",
            color = StockColors.PrimaryBlue,
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier
                .fillMaxWidth()
                .clickable { expanded = !expanded }
                .padding(vertical = 4.dp),
        )

        if (expanded) {
            RecommendStockCardDetails(item = item, hasBuyZone = hasBuyZone, hasSellZone = hasSellZone)
        }

        Text(
            "点击卡片进入该股研究页",
            color = StockColors.Neutral,
            fontSize = 10.sp,
        )
    }
}

/** 默认卡片上一句可读总结 */
private fun conciseRecommendSummary(item: RecommendStockItemDto): String {
    val parts = mutableListOf<String>()
    when {
        item.swingFit ->
            parts += "适合中线3–6个月（吃主升浪，不赚隔夜情绪）"
        item.limitHuntFit ->
            parts += "次日上涨路径（高风险）：不问今日涨停/跌停，只博弈次日收涨；弱开即走"
        item.lowAbsorbRebound && item.overnightFit ->
            parts += "低吸反抽次日博弈：反抽后尾盘确认；明日弱开早走，偏强可尾盘兑现"
        item.volumeDryStop && item.overnightFit ->
            parts += "缩量止跌：近弱后缩量收稳（量比${formatDecimal(item.volumeRatio)}），轻仓隔夜"
        item.sectorLeaderStart && item.overnightFit ->
            parts += "板块龙头启动：板块/龙头转暖跟随，勿追暴拉"
        item.morningDipRebound && item.overnightFit ->
            parts += "早盘深跌翻红：早低${formatDecimal(item.morningLowChangePct, 1)}%→现${formatDecimal(item.changePct)}%，卖区锚定昨收准点兑现"
        item.deepRebound && item.overnightFit ->
            parts += "深跌反抽隔夜：日低${formatDecimal(item.dayLowChangePct, 1)}%→现${formatDecimal(item.changePct)}%（回升${formatDecimal(item.reboundPts, 1)}点）"
        item.overnightFit ->
            parts += "适合隔夜短线（今买明日卖，早盘或尾盘均可出场）"
        else ->
            parts += "风格匹配一般，宜观望或更小仓"
    }
    if (item.sellUpsidePct >= 3.0 && item.sellTargetPrice > 0) {
        parts += "卖出空间约 +${formatDecimal(item.sellUpsidePct)}%（参考 ${formatDecimal(item.sellTargetPrice)}）"
    }
    if (item.chaseRisk) parts += "当日涨幅偏高，勿追高"
    val tendency = Regex("操作倾向：([^。]+)").find(item.analysis)?.groupValues?.getOrNull(1)?.trim()
    if (!tendency.isNullOrBlank()) {
        parts += tendency
    } else if (item.overnightExitHint.isNotBlank()) {
        parts += item.overnightExitHint.take(48)
    }
    if (item.fundLabel.isNotBlank()) parts += item.fundLabel.take(28)
    return parts.distinct().joinToString("；")
}

@Composable
private fun RecommendStockCardDetails(
    item: RecommendStockItemDto,
    hasBuyZone: Boolean,
    hasSellZone: Boolean,
) {
    Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            if (item.sellUpsidePct >= 3.0) {
                TagChip("卖出≥${formatDecimal(item.sellUpsidePct, 1)}%", StockColors.UpRed, StockColors.UpSoft)
            }
            if (item.monthTrendScore >= 1.5) {
                TagChip("近月偏强", StockColors.UpRed, StockColors.UpSoft)
            }
            if (item.monthTrendLabel.contains("回升") || item.monthTrendLabel.contains("抬升")) {
                TagChip("近月回升", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.monthTrendLabel.contains("透支") || item.monthReturnPct >= 18) {
                TagChip("近月偏透支", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.halfYearLabel.contains("高潮") || item.halfReturnPct >= 40) {
                TagChip("半年偏高潮", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.yesterdayReturnPct >= 4.5) {
                TagChip("高潮次日", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.tapeLabel.contains("线上阴线")) {
                TagChip("线上阴线买", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.tapeLabel.contains("线下阳线")) {
                TagChip("线下阳线不买", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.tapeLabel.contains("无量上涨")) {
                TagChip("无量上涨", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.tapeLabel.contains("高位放量")) {
                TagChip("高位放量", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.tapeLabel.contains("震荡市")) {
                TagChip("震荡市", StockColors.Neutral, Color(0xFFF3F4F6))
            }
            if (item.windowLabel.contains("早盘回落")) {
                TagChip("早盘不追", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.windowLabel.contains("尾盘急拉")) {
                TagChip("尾盘急拉不跟", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.windowLabel.contains("午后回踩")) {
                TagChip("午后回踩", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.seasonLabel.contains("钱荒") || item.seasonLabel.contains("业绩雷") ||
                item.seasonLabel.contains("中报")
            ) {
                TagChip("年度避雷", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.chaseRisk) {
                TagChip("勿追高", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.klineStale) {
                TagChip("K线偏旧·请刷新", StockColors.Orange, Color(0xFFFFF4E5))
            }
        }
        if (item.overnightExitHint.isNotBlank()) {
            Text(
                "出场：${item.overnightExitHint}",
                color = StockColors.PrimaryBlue,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.sellUpsidePct > 0 && item.sellTargetPrice > 0) {
            Text(
                "卖出空间：现价 ${formatDecimal(item.price)} → 卖区 ${formatDecimal(item.sellTargetPrice)}" +
                    "（+${formatDecimal(item.sellUpsidePct)}%，门槛≥${if (item.swingFit) "6" else "3.5"}%）",
                color = if (item.sellUpsidePct >= 3.0) StockColors.UpRed else StockColors.Orange,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.asOf.isNotBlank()) {
            Text(
                "K线 asOf ${item.asOf}",
                color = if (item.klineStale) StockColors.Orange else StockColors.TextSecondary,
                fontSize = 10.sp,
            )
        }

        RecommendQuoteMetrics(item)
        if (item.volume > 0) {
            Text(
                "成交量 ${formatDecimal(item.volume, 0)}手",
                color = StockColors.TextSecondary,
                fontSize = 10.sp,
            )
        }
        if (item.klinePosition.isNotBlank()) {
            Text(
                "K线：${item.klinePosition}",
                color = if (item.nearHigh) StockColors.Orange else StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = if (item.nearHigh) FontWeight.SemiBold else FontWeight.Normal,
            )
        }
        if (item.monthTrendLabel.isNotBlank() || item.monthReturnPct != 0.0) {
            Text(
                buildString {
                    append("近1个月：${item.monthTrendLabel.ifBlank { "—" }}")
                    append(" · 月涨跌 ${formatDecimal(item.monthReturnPct)}%")
                    if (item.recent5ReturnPct != 0.0) {
                        append(" · 近5日 ${formatDecimal(item.recent5ReturnPct)}%")
                    }
                },
                color = when {
                    item.monthTrendScore >= 1.5 -> StockColors.UpRed
                    item.monthTrendScore <= -1.0 -> StockColors.DownGreen
                    else -> StockColors.TextPrimary
                },
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.halfYearLabel.isNotBlank() || item.halfReturnPct != 0.0 || item.yesterdayReturnPct != 0.0) {
            Text(
                buildString {
                    append("近半年：${item.halfYearLabel.ifBlank { "—" }}")
                    append(" · 半年涨跌 ${formatDecimal(item.halfReturnPct)}%")
                    append(" · 昨涨 ${formatDecimal(item.yesterdayReturnPct)}%")
                },
                color = when {
                    item.halfYearLabel.contains("高潮") || item.yesterdayReturnPct >= 4.5 -> StockColors.Orange
                    item.halfReturnPct < -15 -> StockColors.DownGreen
                    else -> StockColors.TextPrimary
                },
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.holdPeriodLabel.isNotBlank() || item.ret3mPct != 0.0 || item.ret6mPct != 0.0) {
            Text(
                buildString {
                    append("中线：${item.holdPeriodLabel.ifBlank { "—" }}")
                    append(" · 近3月 ${formatDecimal(item.ret3mPct)}%")
                    append(" · 近6月 ${formatDecimal(item.ret6mPct)}%")
                },
                color = if (item.swingFit) StockColors.PrimaryBlue else StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.tapeLabel.isNotBlank() || item.tapeChecklist.isNotEmpty()) {
            Text(
                buildString {
                    append("涨跌经验：${item.tapeLabel.ifBlank { "—" }}")
                    item.tapeChecklist.firstOrNull()?.let {
                        append(" · ")
                        append(it)
                    }
                },
                color = when {
                    item.tapeLabel.contains("高位放量") || item.tapeLabel.contains("线下阳线") ||
                        item.tapeLabel.contains("无量") -> StockColors.Orange
                    item.tapeLabel.contains("线上阴线") -> StockColors.PrimaryBlue
                    else -> StockColors.TextPrimary
                },
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.seasonLabel.isNotBlank() || item.windowLabel.isNotBlank()) {
            Text(
                buildString {
                    if (item.seasonLabel.isNotBlank()) append(item.seasonLabel)
                    if (item.windowLabel.isNotBlank()) {
                        if (item.seasonLabel.isNotBlank()) append(" · ")
                        append(item.windowLabel)
                    }
                },
                color = when {
                    item.windowLabel.contains("急拉") || item.windowLabel.contains("回落") ||
                        item.seasonLabel.contains("避雷") || item.seasonLabel.contains("钱荒") ||
                        item.seasonLabel.contains("业绩雷") -> StockColors.Orange
                    item.windowLabel.contains("企稳") || item.windowLabel.contains("回踩") ->
                        StockColors.PrimaryBlue
                    else -> StockColors.TextPrimary
                },
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.structureLabel.isNotBlank()) {
            Text("日K结构：${item.structureLabel}", color = StockColors.TextPrimary, fontSize = 11.sp)
        }
        if (item.intradayLabel.isNotBlank() && item.intradayLabel != "分时样本不足") {
            Text("分时：${item.intradayLabel}", color = StockColors.TextPrimary, fontSize = 11.sp)
        }
        if (item.fundLabel.isNotBlank() || item.todayMainNet != 0.0 || item.recentMainNet != 0.0) {
            val fundText = item.fundLabel.ifBlank {
                val todayYi = item.todayMainNet / 1e8
                "今日主力净流入 ${formatDecimal(todayYi)}亿"
            }
            Text(
                fundText,
                color = when {
                    item.todayMainNet > 0 || item.fundLabel.contains("流入") -> StockColors.UpRed
                    item.todayMainNet < 0 || item.fundLabel.contains("流出") -> StockColors.DownGreen
                    else -> StockColors.TextSecondary
                },
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.onLhb) {
            val netYi = item.lhbNetAmt / 1e8
            Text(
                "龙虎榜 ${item.lhbDate.ifBlank { "--" }} 净买 ${formatDecimal(netYi)}亿" +
                    if (item.lhbReason.isNotBlank()) " · ${item.lhbReason.take(24)}" else "",
                color = if (item.lhbNetAmt >= 0) StockColors.UpRed else StockColors.DownGreen,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (item.financeReport.isNotBlank() || item.pe != 0.0 || item.eps != 0.0 || item.revenueYoY != 0.0) {
            Text(
                buildString {
                    if (item.financeReport.isNotBlank()) append(item.financeReport)
                    if (item.eps != 0.0) append(" · EPS ${formatDecimal(item.eps)}")
                    if (item.roe != 0.0) append(" · ROE ${formatDecimal(item.roe)}%")
                    if (item.revenueYoY != 0.0) append(" · 营收同比 ${formatDecimal(item.revenueYoY)}%")
                    if (item.profitYoY != 0.0) append(" · 净利同比 ${formatDecimal(item.profitYoY)}%")
                    if (item.pe != 0.0) append(" · 市盈 ${formatDecimal(item.pe, 1)}")
                    if (item.pb != 0.0) append(" · PB ${formatDecimal(item.pb)}")
                },
                color = when {
                    item.financeScore >= 1.0 -> StockColors.DownGreen
                    item.financeScore <= -1.0 -> StockColors.Orange
                    else -> StockColors.TextSecondary
                },
                fontSize = 11.sp,
            )
        }
        if (item.industry.isNotBlank()) {
            Text(
                "行业：${item.industry}" +
                    (if (item.industryTrend.isNotBlank()) " · ${item.industryTrend}" else "") +
                    (if (item.industryRank > 0) " · 排名${item.industryRank}" else "") +
                    (if (item.sectorLeaderCode.isNotBlank()) {
                        " · 龙头${item.sectorLeaderName.ifBlank { item.sectorLeaderCode }}" +
                            " ${formatDecimal(item.sectorLeaderChangePct, 2)}%"
                    } else {
                        ""
                    }),
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
        }
        if (item.sessionChecklist.isNotEmpty()) {
            Text(
                "盘中检查（分${formatDecimal(item.sessionScore, 1)}）",
                color = StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            item.sessionChecklist.take(10).forEach {
                Text("· $it", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
            }
        }
        if (item.backgroundChecklist.isNotEmpty()) {
            Text(
                "背景检查·二～五（分${formatDecimal(item.backgroundScore, 1)}）",
                color = StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            item.backgroundChecklist.take(14).forEach {
                Text("· $it", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
            }
        }
        if (item.overnightHints.isNotEmpty()) {
            Text(
                "隔夜指引",
                color = StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            item.overnightHints.take(4).forEach {
                Text("· $it", color = StockColors.Orange, fontSize = 10.sp, lineHeight = 14.sp)
            }
        }
        Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            if (item.nearHigh) {
                TagChip("接近高点", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.structureLabel.contains("回踩")) {
                TagChip("回踩企稳", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.structureLabel.contains("放量突破") && !item.nearHigh) {
                TagChip("放量突破", StockColors.DownGreen, StockColors.DownSoft)
            }
            if (item.fundLabel.contains("流入") && !item.nearHigh) {
                TagChip("主力净流入", StockColors.UpRed, StockColors.UpSoft)
            }
            if (item.fundLabel.contains("流出")) {
                TagChip("主力净流出", StockColors.DownGreen, StockColors.DownSoft)
            }
            if (item.onLhb && item.lhbNetAmt > 0) {
                TagChip("龙虎净买", StockColors.UpRed, StockColors.UpSoft)
            }
            if (item.onLhb && item.lhbNetAmt < 0) {
                TagChip("龙虎净卖", StockColors.DownGreen, StockColors.DownSoft)
            }
            if (item.intradayLabel.contains("午后走强")) {
                TagChip("午后走强", StockColors.PrimaryBlue, Color(0xFFEEF4FF))
            }
            if (item.intradayLabel.contains("冲高回落")) {
                TagChip("冲高回落", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.intradayLabel.contains("尾盘急拉") || item.sessionChecklist.any { it.contains("尾盘：急拉") }) {
                TagChip("尾盘急拉", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.structureLabel.contains("放量滞涨") || item.backgroundChecklist.any { it.contains("放量滞涨") }) {
                TagChip("放量滞涨", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.structureLabel.contains("均线空头") || item.backgroundChecklist.any { it.contains("空头排列") }) {
                TagChip("均线空头", StockColors.Orange, Color(0xFFFFF4E5))
            }
            if (item.financeScore >= 1.0) {
                TagChip("业绩偏强", StockColors.DownGreen, StockColors.DownSoft)
            }
            if (item.financeScore <= -1.0) {
                TagChip("业绩/估值偏弱", StockColors.Orange, Color(0xFFFFF4E5))
            }
        }

        if (item.analysis.isNotBlank()) {
            Text(
                item.analysis,
                color = StockColors.TextPrimary,
                fontSize = 12.sp,
                lineHeight = 17.sp,
            )
        }

        if (item.latestAnnouncements.isNotEmpty()) {
            Text("最新公告", color = StockColors.TextPrimary, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
            item.latestAnnouncements.take(3).forEach {
                Text("· $it", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
            }
        }
        if (item.latestNews.isNotEmpty()) {
            Text("最新新闻", color = StockColors.TextPrimary, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
            item.latestNews.take(3).forEach {
                Text("· $it", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
            }
        }
        if (item.reorgLabel.isNotBlank() || item.newsEventTags.isNotEmpty()) {
            Text("资讯/重组分析", color = StockColors.TextPrimary, fontSize = 11.sp, fontWeight = FontWeight.SemiBold)
            if (item.reorgLabel.isNotBlank()) {
                Text(
                    item.reorgLabel,
                    color = StockColors.Orange,
                    fontSize = 10.sp,
                    lineHeight = 14.sp,
                )
            }
            if (item.newsEventTags.isNotEmpty()) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    modifier = Modifier.horizontalScroll(rememberScrollState()),
                ) {
                    item.newsEventTags.take(6).forEach { tag ->
                        val warn = tag.contains("失败") || tag.contains("利空") ||
                            tag.contains("减持") || tag.contains("停牌") || tag.contains("监管")
                        TagChip(
                            tag,
                            if (warn) StockColors.Orange else StockColors.PrimaryBlue,
                            if (warn) Color(0xFFFFF4E5) else Color(0xFFE8F0FE),
                        )
                    }
                }
            }
        }

        if (item.technicalScore > 0 || item.liquidityScore > 0 || item.dayScore > 0) {
            Text(
                "得分 技术${formatDecimal(item.technicalScore, 0)} · 流动${formatDecimal(item.liquidityScore, 0)} · 当日${formatDecimal(item.dayScore, 0)} · 盘中${formatDecimal(item.sessionScore, 1)} · 背景${formatDecimal(item.backgroundScore, 1)} · 资讯${formatDecimal(item.newsScore, 0)} · 风险项${formatDecimal(item.riskScore, 0)}",
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
        }

        if (hasBuyZone && (item.buyMethod.isNotBlank() || item.buyConfidence > 0)) {
            Text(
                listOfNotNull(
                    item.buyMethod.takeIf { it.isNotBlank() },
                    item.buyConfidence.takeIf { it > 0 }?.let { "买入置信 ${(it * 100).toInt()}%" },
                ).joinToString(" · "),
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
        }
        if (item.buyHint.isNotBlank()) {
            Text(item.buyHint, color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 15.sp)
        }
        item.buyReasons.take(3).forEach { reason ->
            Text("· $reason", color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 15.sp)
        }

        if (hasSellZone && (item.sellMethod.isNotBlank() || item.sellConfidence > 0)) {
            Text(
                listOfNotNull(
                    item.sellMethod.takeIf { it.isNotBlank() },
                    item.sellConfidence.takeIf { it > 0 }?.let { "卖出置信 ${(it * 100).toInt()}%" },
                ).joinToString(" · "),
                color = StockColors.TextSecondary,
                fontSize = 11.sp,
            )
        }
        if (item.sellPerspectives.isNotEmpty()) {
            Text(
                "卖出多方视角",
                color = StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            item.sellPerspectives.take(8).forEach { p ->
                Text("· $p", color = StockColors.TextSecondary, fontSize = 10.sp, lineHeight = 14.sp)
            }
        }
        if (item.sellHint.isNotBlank()) {
            Text(item.sellHint, color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 15.sp)
        }
        item.sellReasons.take(6).forEach { reason ->
            Text("· $reason", color = StockColors.TextSecondary, fontSize = 11.sp, lineHeight = 15.sp)
        }

        val displayReasons = item.reasons.filterNot {
            it.startsWith("建议买入") || it.startsWith("建议卖出")
        }
        if (displayReasons.isNotEmpty()) {
            Text(
                "推荐理由",
                color = StockColors.TextPrimary,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
            )
            displayReasons.forEach { reason ->
                Text(
                    "· $reason",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                    lineHeight = 16.sp,
                )
            }
        }

        if (item.risks.isNotEmpty()) {
            Text(
                "风险提示：${item.risks.joinToString("；")}",
                color = StockColors.Orange,
                fontSize = 10.sp,
                lineHeight = 14.sp,
            )
        }
    }
}

/**
 * 分析进行中：桌面左右分栏；手机竖排整页可滚，避免挤掉筛选顶栏与动态区。
 */
@Composable
private fun RecommendProgressLivePane(
    progress: RecommendProgressDto?,
    boardsLabel: String,
    modifier: Modifier = Modifier,
) {
    val pct = (progress?.percent ?: 0).coerceIn(0, 100)
    val stage = progress?.stage?.ifBlank { "筛选与打分" } ?: "筛选与打分"
    val detail = progress?.detail.orEmpty()
    val steps = progress?.steps.orEmpty()
    val log = progress?.log.orEmpty().ifEmpty {
        listOfNotNull(
            if (stage.isNotBlank() || detail.isNotBlank()) {
                if (detail.isBlank()) stage else "$stage · $detail"
            } else {
                null
            },
        )
    }
    val preview = progress?.preview.orEmpty()
    val logState = rememberLazyListState()
    LaunchedEffect(log.size) {
        if (log.isNotEmpty()) {
            logState.animateScrollToItem(log.lastIndex)
        }
    }

    BoxWithConstraints(modifier = modifier.padding(vertical = 4.dp)) {
        val phone = isPhoneWidth(maxWidth)
        Column(
            modifier = Modifier.fillMaxWidth().fillMaxHeight(),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Text(
                "正在筛选与打分 · $pct%",
                color = StockColors.PrimaryBlue,
                fontSize = 15.sp,
                fontWeight = FontWeight.Bold,
            )
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(8.dp)
                    .background(Color(0xFFE8EEF7), RoundedCornerShape(999.dp)),
            ) {
                Spacer(
                    modifier = Modifier
                        .fillMaxWidth(pct / 100f)
                        .height(8.dp)
                        .background(StockColors.PrimaryBlue, RoundedCornerShape(999.dp)),
                )
            }
            Text(
                "当前：$stage" + if (detail.isNotBlank()) " · $detail" else "",
                color = StockColors.TextPrimary,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
            )

            if (phone) {
                // 手机：单列可滚，步骤/动态/预览都能滑到
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f)
                        .verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    ProgressStepsBlock(steps = steps, stage = stage)
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(Color(0xFFF8FAFD), RoundedCornerShape(8.dp))
                            .border(BorderStroke(1.dp, Color(0xFFE2E8F0)), RoundedCornerShape(8.dp))
                            .padding(10.dp),
                        verticalArrangement = Arrangement.spacedBy(6.dp),
                    ) {
                        Text(
                            "分析动态（实时更新）",
                            color = StockColors.TextPrimary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            log.forEachIndexed { idx, line ->
                                ProgressLogLine(line, newest = idx == log.lastIndex)
                            }
                        }
                    }
                    ProgressPreviewBlock(preview = preview, fixedHeight = false)
                    Text(
                        "分析「$boardsLabel」全量，动态与预览会持续刷新，完成后自动显示最终名单",
                        color = StockColors.Neutral,
                        fontSize = 11.sp,
                    )
                }
            } else {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    Column(
                        modifier = Modifier
                            .width(132.dp)
                            .fillMaxHeight()
                            .verticalScroll(rememberScrollState()),
                    ) {
                        ProgressStepsBlock(steps = steps, stage = stage)
                    }
                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxHeight()
                            .background(Color(0xFFF8FAFD), RoundedCornerShape(8.dp))
                            .border(BorderStroke(1.dp, Color(0xFFE2E8F0)), RoundedCornerShape(8.dp))
                            .padding(10.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(
                            "分析动态（实时更新）",
                            color = StockColors.TextPrimary,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                        )
                        LazyColumn(
                            state = logState,
                            modifier = Modifier
                                .fillMaxWidth()
                                .weight(1f),
                            verticalArrangement = Arrangement.spacedBy(4.dp),
                        ) {
                            items(log.size) { idx ->
                                ProgressLogLine(log[idx], newest = idx == log.lastIndex)
                            }
                        }
                        ProgressPreviewBlock(preview = preview, fixedHeight = true)
                    }
                }
                Text(
                    "分析「$boardsLabel」全量，右侧动态与预览会持续刷新，完成后自动显示最终名单",
                    color = StockColors.Neutral,
                    fontSize = 11.sp,
                )
            }
        }
    }
}

@Composable
private fun ProgressStepsBlock(
    steps: List<String>,
    stage: String,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFFF3F6FA), RoundedCornerShape(8.dp))
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            "已完成步骤",
            color = StockColors.TextPrimary,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
        )
        if (steps.isEmpty()) {
            Text("· 排队中…", color = StockColors.TextSecondary, fontSize = 11.sp)
        } else {
            steps.forEach { s ->
                val current = s == stage
                Text(
                    "${if (current) "►" else "✓"} $s",
                    color = if (current) StockColors.PrimaryBlue else StockColors.TextSecondary,
                    fontSize = 11.sp,
                )
            }
        }
    }
}

@Composable
private fun ProgressLogLine(line: String, newest: Boolean) {
    Text(
        line,
        color = if (newest) StockColors.PrimaryBlue else StockColors.TextSecondary,
        fontSize = if (newest) 12.sp else 11.sp,
        fontWeight = if (newest) FontWeight.SemiBold else FontWeight.Normal,
        lineHeight = 15.sp,
    )
}

@Composable
private fun ProgressPreviewBlock(
    preview: List<RecommendPreviewItemDto>,
    fixedHeight: Boolean,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(
            if (preview.isEmpty()) "临时预览（细算入围后在此替换更新）"
            else "临时预览 · ${preview.size} 只（随细算替换，非最终结果）",
            color = StockColors.TextPrimary,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .then(if (fixedHeight) Modifier.height(150.dp).verticalScroll(rememberScrollState()) else Modifier),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            if (preview.isEmpty()) {
                Text(
                    "尚无达阈值标的，正在继续检查…",
                    color = StockColors.Neutral,
                    fontSize = 11.sp,
                )
            } else {
                preview.forEach { p ->
                    PreviewStockRow(p)
                }
            }
        }
    }
}

@Composable
private fun PreviewStockRow(
    item: RecommendPreviewItemDto,
    onSelect: (() -> Unit)? = null,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color.White, RoundedCornerShape(6.dp))
            .border(BorderStroke(1.dp, Color(0xFFE8EEF7)), RoundedCornerShape(6.dp))
            .then(if (onSelect != null) Modifier.clickable(onClick = onSelect) else Modifier)
            .padding(horizontal = 8.dp, vertical = 6.dp),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                "${item.name.ifBlank { "--" }} ${item.code}",
                color = StockColors.TextPrimary,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                "分 ${formatDecimal(item.score, 0)}",
                color = StockColors.PrimaryBlue,
                fontSize = 12.sp,
                fontWeight = FontWeight.Bold,
            )
        }
        Text(
            buildString {
                append("涨跌 ${formatDecimal(item.changePct, 2)}%")
                if (item.overnightFit) append(" · 隔夜匹配")
                if (item.note.contains("中线")) append(" · 中线")
                if (item.note.isNotBlank()) append(" · ${item.note}")
            },
            color = StockColors.TextSecondary,
            fontSize = 10.sp,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )
    }
}
